// Alles wat je ooit in een notitie hebt gezet: foto's, schermafdrukken, pdf's.
//
// Nodig omdat een bijlage anders alleen te vinden is via de notitie waar hij in hangt — en je
// weet meestal wel dát je die tekening ergens hebt opgeslagen, niet waar. Hier staat er ook
// bij in welke notities hij voorkomt, zodat je vanaf het bestand terug kunt naar de tekst.

import { useEffect, useState } from "react";
import { api, GeenVerbinding, type BestandRegel } from "../api.ts";
import { Icoon } from "./Icoon.tsx";
import { leesbareGrootte } from "./Bijlage.ts";

/** Wat er van de tekstherkenning terecht is gekomen, in gewone woorden. */
function tekstStand(stand: { stand: string; tekens: number }): string {
  if (stand.stand === "wacht") return "tekst nog niet gelezen";
  if (stand.stand === "klaar") return `${stand.tekens} tekens doorzoekbaar`;
  if (stand.stand === "mislukt") return "tekst lezen is niet gelukt";
  return "geen leesbare tekst gevonden";
}

function datumKort(iso: string): string {
  const datum = new Date(iso);
  return datum.toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" });
}

type Props = {
  terug: () => void;
  opNotitie: (id: string) => void;
};

export function Bijlagen({ terug, opNotitie }: Props) {
  const [regels, setRegels] = useState<BestandRegel[] | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    void (async () => {
      try {
        const { bestanden } = await api.bestanden();
        setRegels(bestanden);
      } catch (f) {
        setRegels([]);
        setFout(
          f instanceof GeenVerbinding
            ? "Zonder verbinding is deze lijst niet op te halen; de bestanden staan op de server."
            : "De bijlagen konden niet worden opgehaald."
        );
      }
    })();
  }, []);

  const zoek = filter.trim().toLowerCase();
  const tonen = (regels ?? []).filter(
    (r) => zoek === "" || r.naam.toLowerCase().includes(zoek) || r.gebruiktIn.some((n) => n.titel.toLowerCase().includes(zoek))
  );
  const totaal = (regels ?? []).reduce((som, r) => som + r.grootte, 0);

  return (
    <div className="bijlagenscherm">
      <div className="bijlagenkop">
        <button className="knop stil" onClick={terug} aria-label="Terug">
          <Icoon naam="terug" grootte={18} />
        </button>
        <h2>Bijlagen</h2>
        <span style={{ flexGrow: 1 }} />
        <input
          className="bijlagenzoek"
          value={filter}
          placeholder="Zoeken op naam of notitie"
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {fout && <p className="leeg">{fout}</p>}
      {regels === null && <p className="leeg">Even ophalen…</p>}
      {regels !== null && regels.length === 0 && !fout && (
        <p className="leeg">Nog geen bijlagen. Plak een schermafdruk in een notitie, of gebruik de paperclip.</p>
      )}

      <div className="bijlagenlijst">
        {tonen.map((regel) => (
          <div key={regel.hash} className="bijlagerij">
            <a className="bijlagevoorbeeld" href={regel.url} download={regel.naam} title={`${regel.naam} downloaden`}>
              {regel.afbeelding ? <img src={regel.url} alt="" loading="lazy" /> : <span className="bijlagesoort">📎</span>}
            </a>
            <div style={{ minWidth: 0, flexGrow: 1 }}>
              <a className="bijlagetitel" href={regel.url} download={regel.naam}>
                {regel.naam}
              </a>
              <div className="bijlagemeta">
                {leesbareGrootte(regel.grootte)} · {regel.mime || "onbekend type"} · {datumKort(regel.aangemaakt)}
                {regel.tekst && <> · {tekstStand(regel.tekst)}</>}
              </div>
              <div className="bijlagemeta">
                {regel.gebruiktIn.length === 0 ? (
                  <span title="Deze bijlage staat in geen enkele notitie meer; hij blijft staan tot je hem opruimt.">
                    nergens meer gebruikt
                  </span>
                ) : (
                  <>
                    in{" "}
                    {regel.gebruiktIn.map((notitie, nr) => (
                      <span key={notitie.id}>
                        {nr > 0 && ", "}
                        <button className="bijlagelink" onClick={() => opNotitie(notitie.id)}>
                          {notitie.titel || "Zonder titel"}
                        </button>
                      </span>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {regels !== null && regels.length > 0 && (
        <p className="bijlagenvoet">
          {regels.length} {regels.length === 1 ? "bestand" : "bestanden"}, samen {leesbareGrootte(totaal)}. Ze staan in{" "}
          <code>data/note/bestanden</code> op de NAS en gaan mee in de nachtelijke back-up.
        </p>
      )}
    </div>
  );
}
